import { supabase } from '../lib/supabase';
import type {
  WebsiteCampaign,
  WebsiteCommunityGalleryItem,
  WebsitePracticalHighlight,
  WebsiteSpotlightCard,
  WebsiteTestimonial,
  website_campaign_seed,
  website_community_gallery_item_seed,
  website_practical_highlight_seed,
  website_spotlight_card_seed,
  website_testimonial_seed,
} from '@pigeons/shared-website-media';

type website_content_table_name =
  | 'website_testimonials'
  | 'website_spotlight_cards'
  | 'website_community_gallery_items'
  | 'website_practical_highlights'
  | 'website_campaigns';

type sort_order_update = {
  id: string;
  sort_order: number;
};

const list_rows = async <T>(tableName: website_content_table_name): Promise<T[]> => {
  const { data, error } = await supabase.from(tableName).select('*').order('sort_order');

  if (error) {
    throw error;
  }

  return (data ?? []) as T[];
};

const create_row = async <T, TPayload extends object>(
  tableName: website_content_table_name,
  payload: TPayload
): Promise<T> => {
  const { data, error } = await supabase.from(tableName).insert(payload).select('*').single();

  if (error) {
    throw error;
  }

  return data as T;
};

const update_row = async <T, TPayload extends object>(
  tableName: website_content_table_name,
  id: string,
  payload: TPayload
): Promise<T> => {
  const { data, error } = await supabase
    .from(tableName)
    .update(payload)
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    throw error;
  }

  return data as T;
};

const delete_row = async (tableName: website_content_table_name, id: string): Promise<void> => {
  const { error } = await supabase.from(tableName).delete().eq('id', id);

  if (error) {
    throw error;
  }
};

const update_sort_orders = async (
  tableName: website_content_table_name,
  updates: sort_order_update[]
): Promise<void> => {
  for (const update of updates) {
    const { error } = await supabase
      .from(tableName)
      .update({ sort_order: update.sort_order })
      .eq('id', update.id);

    if (error) {
      throw error;
    }
  }
};

export const websiteContentRepository = {
  listTestimonials: () => list_rows<WebsiteTestimonial>('website_testimonials'),
  createTestimonial: (payload: website_testimonial_seed) =>
    create_row<WebsiteTestimonial, website_testimonial_seed>('website_testimonials', payload),
  updateTestimonial: (id: string, payload: Partial<website_testimonial_seed>) =>
    update_row<WebsiteTestimonial, Partial<website_testimonial_seed>>(
      'website_testimonials',
      id,
      payload
    ),
  deleteTestimonial: (id: string) => delete_row('website_testimonials', id),
  reorderTestimonials: (updates: sort_order_update[]) =>
    update_sort_orders('website_testimonials', updates),

  listSpotlightCards: () => list_rows<WebsiteSpotlightCard>('website_spotlight_cards'),
  createSpotlightCard: (payload: website_spotlight_card_seed) =>
    create_row<WebsiteSpotlightCard, website_spotlight_card_seed>(
      'website_spotlight_cards',
      payload
    ),
  updateSpotlightCard: (id: string, payload: Partial<website_spotlight_card_seed>) =>
    update_row<WebsiteSpotlightCard, Partial<website_spotlight_card_seed>>(
      'website_spotlight_cards',
      id,
      payload
    ),
  deleteSpotlightCard: (id: string) => delete_row('website_spotlight_cards', id),
  reorderSpotlightCards: (updates: sort_order_update[]) =>
    update_sort_orders('website_spotlight_cards', updates),

  listCommunityGalleryItems: () =>
    list_rows<WebsiteCommunityGalleryItem>('website_community_gallery_items'),
  createCommunityGalleryItem: (payload: website_community_gallery_item_seed) =>
    create_row<WebsiteCommunityGalleryItem, website_community_gallery_item_seed>(
      'website_community_gallery_items',
      payload
    ),
  updateCommunityGalleryItem: (id: string, payload: Partial<website_community_gallery_item_seed>) =>
    update_row<WebsiteCommunityGalleryItem, Partial<website_community_gallery_item_seed>>(
      'website_community_gallery_items',
      id,
      payload
    ),
  deleteCommunityGalleryItem: (id: string) => delete_row('website_community_gallery_items', id),
  reorderCommunityGalleryItems: (updates: sort_order_update[]) =>
    update_sort_orders('website_community_gallery_items', updates),

  listPracticalHighlights: () =>
    list_rows<WebsitePracticalHighlight>('website_practical_highlights'),
  createPracticalHighlight: (payload: website_practical_highlight_seed) =>
    create_row<WebsitePracticalHighlight, website_practical_highlight_seed>(
      'website_practical_highlights',
      payload
    ),
  updatePracticalHighlight: (id: string, payload: Partial<website_practical_highlight_seed>) =>
    update_row<WebsitePracticalHighlight, Partial<website_practical_highlight_seed>>(
      'website_practical_highlights',
      id,
      payload
    ),
  deletePracticalHighlight: (id: string) => delete_row('website_practical_highlights', id),
  reorderPracticalHighlights: (updates: sort_order_update[]) =>
    update_sort_orders('website_practical_highlights', updates),

  listCampaigns: async (): Promise<WebsiteCampaign[]> => {
    const { data, error } = await supabase
      .from('website_campaigns')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      throw error;
    }

    return (data ?? []) as WebsiteCampaign[];
  },
  getCampaignBySlug: async (slug: string): Promise<WebsiteCampaign | null> => {
    const { data, error } = await supabase
      .from('website_campaigns')
      .select('*')
      .eq('slug', slug)
      .maybeSingle();

    if (error) {
      throw error;
    }

    return (data ?? null) as WebsiteCampaign | null;
  },
  createCampaign: (payload: website_campaign_seed) =>
    create_row<WebsiteCampaign, website_campaign_seed>('website_campaigns', payload),
  updateCampaign: (id: string, payload: Partial<website_campaign_seed>) =>
    update_row<WebsiteCampaign, Partial<website_campaign_seed>>('website_campaigns', id, payload),
  deleteCampaign: (id: string) => delete_row('website_campaigns', id),
};
